import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { catchError, switchMap } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import {
  FormGroupType,
  FormTypeEnum,
  MailResponse,
  RequestStatus,
} from './forms.types';
import {
  API_FREE_TRIAL,
  API_SUBSCRIBE,
  FORM_TYPE,
  FREE_TRIAL_FORM,
  SUBSCRIBE_FORM,
} from './forms.constants';

@Injectable()
export class FormsService<T extends FormTypeEnum> {
  private readonly recaptcha3 = inject(ReCaptchaV3Service);
  private readonly http = inject(HttpClient);
  private readonly formType = inject(FORM_TYPE);

  private readonly status = signal<RequestStatus>('init');
  public readonly form = this.createForm();

  get requestStatus() {
    return this.status.asReadonly();
  }

  public resetStatus(withDelay = true) {
    setTimeout(() => this.status.set('init'), withDelay ? 5000 : 0);
  }

  public submit(place: string, isMuseum?: boolean) {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.status.set('pending');
    this.form.disable();

    const recaptchaAction =
      this.formType === FormTypeEnum.FREE_TRIAL
        ? 'free_trial_demo'
        : 'newsletter_subscribe';

    this.recaptcha3
      .execute(recaptchaAction)
      .pipe(
        switchMap((token) => {
          const body: Record<string, unknown> = {
            ...this.form.getRawValue(),
            place,
            recaptchaToken: token,
          };

          if (this.formType === FormTypeEnum.FREE_TRIAL) {
            body.isMuseum = !!isMuseum;
          }

          const url =
            this.formType === FormTypeEnum.FREE_TRIAL
              ? API_FREE_TRIAL
              : API_SUBSCRIBE;
          return this.request$<MailResponse>(url, body);
        }),
        catchError((error) => {
          this.onError(error);
          return EMPTY;
        }),
      )
      .subscribe((data) => {
        if (data.success) {
          this.form.reset();
          this.form.enable();
          this.status.set('success');
          if (this.formType === FormTypeEnum.FREE_TRIAL) {
            this.callWindowScript();
          }
        } else {
          this.onError(data['error-codes']);
        }
      });
  }

  private request$<T>(endpoint: string, body: unknown) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });
    return this.http.post<T>(endpoint, body, { headers });
  }

  private onError(message: unknown) {
    console.error('Form error', message);
    this.status.set('error');
    this.form.enable();
    this.resetStatus();
  }

  private createForm(): FormGroupType<T> {
    const group =
      this.formType === FormTypeEnum.FREE_TRIAL
        ? FREE_TRIAL_FORM
        : SUBSCRIBE_FORM;
    return group as FormGroupType<T>;
  }

  private callWindowScript() {
    if (window.gtag_report_conversion && window.gtag) {
      window.gtag_report_conversion();
    }
  }
}
